import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ISubscription } from 'models/subscriptions.model'
import { CURRENCY_SYMBOLS, PurchaseProduct } from 'modules/purchase/constants'
import checkIcon from 'assets/images/sprite/check-icon.svg'
import { SvgImage } from 'components/SvgImage'
import { StyledSubscriptionItem as S } from './SubscriptionItem.styles'

type TProps = {
  subscription: ISubscription
  isSelected: boolean
}

export const SubscriptionItem: React.FC<TProps> = ({
  subscription: {
    product,
    periodQuantity,
    currentPrice,
    trialPeriodDays,
    trialCurrentPrice,
    oldPrice,
    oldPriceSecond,
    priceId,
    isDefault,
    customPrice,
    customPriceInterval,
    currency,
  },
  isSelected,
}) => {
  const { t } = useTranslation()

  const oldPriceWithFractionalPart = useMemo(
    () => oldPrice.toFixed(2),
    [oldPrice],
  )

  const oldPriceSecondWithFractionalPart = useMemo(
    () => oldPriceSecond.toFixed(2),
    [oldPriceSecond],
  )

  const currentPriceWithFractionalPart = useMemo(
    () => (trialCurrentPrice || currentPrice).toFixed(2),
    [trialCurrentPrice, currentPrice],
  )

  const [integerPrice, decimalPrice] = useMemo(
    () => String(customPrice).split('.'),
    [customPrice],
  )

  return (
    <>
      {isDefault && (
        <S.MostPopularBadge>{t`purchase.subscription.mostPopularBadge`}</S.MostPopularBadge>
      )}
      <S.Wrapper key={priceId} isSelected={isSelected} isDefault={isDefault}>
        <S.Content>
          <S.SelectionOption isSelected={isSelected}>
            <SvgImage svg={checkIcon} width={30} />
          </S.SelectionOption>
          <S.PlanContainer>
            <S.PlanPeriod isSelected={isSelected}>
              {product === PurchaseProduct.SEVEN_DAY
                ? t('purchase.subscription.weeklyPeriod', { trialPeriodDays })
                : t('purchase.subscription.monthlyPeriod', {
                    periodQuantity,
                  })}
            </S.PlanPeriod>
            <S.PriceContainer>
              <S.PreviousPrice>
                {CURRENCY_SYMBOLS[currency]}
                {oldPriceWithFractionalPart}
              </S.PreviousPrice>
              <S.CurrentPrice>
                {CURRENCY_SYMBOLS[currency]}
                {currentPriceWithFractionalPart}
              </S.CurrentPrice>
            </S.PriceContainer>
          </S.PlanContainer>
        </S.Content>

        <S.Content>
          <S.PreviousPriceCrossed>
            {CURRENCY_SYMBOLS[currency]}
            {oldPriceSecondWithFractionalPart}
          </S.PreviousPriceCrossed>
          <S.CustomPrice isSelected={isSelected}>
            <S.Currency>{CURRENCY_SYMBOLS[currency]}</S.Currency>
            <S.PriceValue>{integerPrice}</S.PriceValue>
            <S.DecimalPrice>
              <span>{decimalPrice}</span>
              <S.Period>
                {t`purchase.subscription.interval`}{' '}
                {t(`purchase.subscription.${customPriceInterval}` as any)}
              </S.Period>
            </S.DecimalPrice>
          </S.CustomPrice>
        </S.Content>
      </S.Wrapper>
    </>
  )
}
